import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/api";

/**
 * Auto-updating version badge. Fetches /api/system/version once on mount so
 * the version + updated-at timestamp always reflect the currently running
 * build. Safe to render on public + authenticated pages.
 *
 * Usage:
 *   <VersionBadge />                         // compact, one line
 *   <VersionBadge variant="dark" />          // dark background footer
 *   <VersionBadge showRelease />             // include release codename
 */
export default function VersionBadge({ variant = "light", showRelease = false, className = "" }) {
  const [v, setV] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND_URL}/api/system/version`, { credentials: "omit" })
      .then((r) => r.json())
      .then((d) => { if (alive) setV(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!v) return null;

  const updated = v.updated_at ? new Date(v.updated_at) : null;
  const stamp = updated
    ? updated.toISOString().slice(0, 16).replace("T", " ") + " UTC"
    : "";

  const tone = variant === "dark"
    ? "text-slate-400 hover:text-slate-200"
    : "text-slate-500 hover:text-slate-700";

  return (
    <span
      className={`text-[10px] font-mono tracking-wide ${tone} ${className}`}
      title={`Updated ${stamp}${v.release_name ? ` · ${v.release_name}` : ""}`}
      data-testid="version-badge"
    >
      v{v.version}
      {showRelease && v.release_name ? ` · ${v.release_name}` : ""}
      <span className="mx-1 opacity-40">·</span>
      {stamp}
    </span>
  );
}
